import { Text, View } from 'react-native';
import { formatWeight, type SetProposal } from '@heraion/shared';

import { DisclosureCard } from '../primitives/DisclosureCard';
import { weightUnitLabels } from '../weightUnitLabels';
import type { useTheme } from './theme';
import type { WeightUnit } from './state/loggerDraft';
import { HistoryCopyRow } from './_HistoryCopyRow';
import { ProposalRows } from './_ProposalRows';
import type { WeightCandidateRow } from './weightCandidateRows';
import type { HistoryPanelStyles } from './HistoryPanel.styles';

// LG-59, section 1 of the history widget — the weights the app worked out for
// the set in hand, the one in the cell on top and marked «in uso».
//
// ⭐ Its own file for two reasons that agree: HistoryPanel crossed the 500
// cap with it inline, and this is the one section that needs no history at
// all — so the visual bench can mount it directly, with no provider to fake.
//
// ⛔ EVERY row carries «Copia», the one in use included (Antenore,
// 2026-09-02: «il copy è il meccanismo, la linea è la spiegazione»). On the
// row in use it puts the computed number back after a manual edit.
export function WeightCandidateSection({
  rows,
  onUse,
  proposals = [],
  onCopyProposal,
  getExerciseNameBySlug = (slug) => slug,
  copyLabel,
  locale,
  unit,
  styles,
  theme,
  t,
}: {
  rows: WeightCandidateRow[];
  /** Put this weight on the set in hand. Absent = nothing open to write to. */
  onUse?: (kg: number) => void;
  /** Sets worked out from the athlete's own history, rendered after the rows. */
  proposals?: SetProposal[];
  onCopyProposal?: (patch: { weightKg: number; reps: number }) => void;
  getExerciseNameBySlug?: (slug: string, fallback?: string) => string;
  copyLabel: string;
  locale: string;
  unit: WeightUnit;
  styles: HistoryPanelStyles;
  theme: ReturnType<typeof useTheme>;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const unitLabels = weightUnitLabels(t);
  const title = t('mobile.logger.historySectionComputed', {
    defaultValue: 'Where this weight comes from',
  });
  return (
    <DisclosureCard
      theme={theme}
      initialExpanded
      accessibilityLabel={title}
      header={<Text style={styles.headerLabel}>{title}</Text>}
      bodyStyle={styles.rowStack}
    >
      {rows.map((c) => (
        <HistoryCopyRow
          key={`${c.source}-${c.kg}`}
          styles={styles}
          theme={theme}
          highlighted={c.inUse}
          copyLabel={copyLabel}
          copyA11yLabel={t('mobile.logger.historyCopyWeightA11y', {
            defaultValue: "Use this weight on the set you're on",
          })}
          onCopy={onUse ? () => onUse(c.kg) : undefined}
        >
          <View style={styles.candidateHead}>
            <Text style={styles.candidateWeight}>
              {formatWeight(c.kg, unit, locale, unitLabels)}
            </Text>
            {/* ⛔ The word, not only the tinted edge: the athlete this is
                built for does not read a 3px border. */}
            {c.inUse ? (
              <Text style={styles.inUseBadge}>
                {t('mobile.logger.historyInUse', { defaultValue: 'in use' })}
              </Text>
            ) : null}
          </View>
          <Text
            style={styles.candidateLabel}
            accessibilityHint={c.hint}
            numberOfLines={2}
          >
            {c.label}
          </Text>
        </HistoryCopyRow>
      ))}
      <ProposalRows
        proposals={proposals}
        onCopySet={onCopyProposal}
        getExerciseNameBySlug={getExerciseNameBySlug}
        copyLabel={copyLabel}
        locale={locale}
        unit={unit}
        styles={styles}
        theme={theme}
        t={t}
      />
    </DisclosureCard>
  );
}
